import DtoolsReviewHierarchy from "@/components/sales/dtools/DtoolsReviewHierarchy";
import type { DtoolsOptionSectionActionsProps } from "@/components/sales/dtools/DtoolsOptionSectionActions";
import type { DtoolsProposalCoverInput } from "@/components/sales/dtools/DtoolsReviewProposalPreview";
import type { ReactNode } from "react";
import type { DtoolsLiveProposalViewModel } from "@shared/dtoolsProposalLiveView";
import type { ProposalCustomerPricingDisplay } from "@shared/proposalCustomerPricing";
import { resolveProposalTitle } from "@shared/dtoolsProposalPresentation";
import type { DtoolsPdfPackageMeta } from "@shared/dtoolsPdfPackage";

type Props = {
  model: DtoolsLiveProposalViewModel;
  audience?: "customer" | "staff";
  customerPricing?: ProposalCustomerPricingDisplay | null;
  optionActions?: Omit<DtoolsOptionSectionActionsProps, "optionLabel">;
  onOpenCleo?: () => void;
  contentLead?: ReactNode;
  retailPricingJson?: {
    listTotal?: number | null;
    originalListPrice?: number | null;
    agreedPriceSgd?: number | null;
    agreedPrice?: number | null;
  } | null;
  totalAmount?: number | string | null;
  pdfPackageMeta?: DtoolsPdfPackageMeta;
};

/** Live Link A / Link B — same renderer tree as Step 3 sandbox preview (`DtoolsReviewHierarchy`). */
export default function DtoolsLiveProposalContent({
  model,
  audience = "customer",
  customerPricing,
  optionActions,
  onOpenCleo,
  contentLead,
  retailPricingJson,
  totalAmount,
  pdfPackageMeta,
}: Props) {
  const customerName =
    String(model.headerPresentation.customerNameSnapshot ?? model.parseResult.customer?.clientName ?? "").trim() ||
    "Valued Customer";

  const cover: DtoolsProposalCoverInput = {
    customerName,
    proposalTitle: resolveProposalTitle(model.headerPresentation, model.presentation),
    proposalRef:
      String(model.headerPresentation.proposalRef ?? model.headerPresentation.projectNumber ?? "—").trim() || "—",
  };

  const resolvedPdfMeta: DtoolsPdfPackageMeta | undefined = pdfPackageMeta
    ? {
        ...pdfPackageMeta,
        address:
          pdfPackageMeta.address ??
          model.parseResult?.customer?.siteAddress ??
          null,
      }
    : {
        address: model.parseResult?.customer?.siteAddress ?? null,
      };

  // Inline System→Room→Primary/Supporting VO annotations are the authoritative audit trail.
  // Global "Previous Items" was removed to avoid duplicating removed products outside hierarchy.

  return (
    <DtoolsReviewHierarchy
      systems={model.systems}
      footer={model.footer}
      parseResult={model.parseResult}
      paymentTerms={model.paymentTerms}
      displaySettings={model.displaySettings}
      systemContentMap={model.systemContentMap}
      presentation={model.presentation}
      headerPresentation={model.headerPresentation}
      systemOverviewAnnotations={model.systemOverviewAnnotations}
      audience={audience}
      cleoDescriptionPendingMessage={audience === "staff" ? "Cleo description not fetched yet" : undefined}
      showApprovalPlaceholder={false}
      cover={cover}
      tabModelVersion={model.tabModelVersion}
      systemClassifications={model.systemClassifications}
      customerPricing={customerPricing}
      optionActions={
        optionActions
          ? {
              ...optionActions,
              onOpenCleo,
            }
          : undefined
      }
      contentLead={contentLead}
      retailPricingJson={retailPricingJson}
      totalAmount={totalAmount}
      pdfPackageMeta={resolvedPdfMeta}
      linkBExpiresAt={model.headerPresentation.linkBExpiresAt ?? null}
    />
  );
}
